import React, { useState } from "react";
import {
  Typography,
  Box,
  Chip,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Collapse,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CancelSchedule from "./cancelSchedule";
import { GroupBookingsByDateAndTimeRange } from "./groupBookingsByDateAndTimeRange";

const BookingRecord = ({
  currentYear,
  bookingType,
  bookingsArray,
  getBookingDetail,
  answersData,
  timezone,
}) => {
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  // Global toggle for collapse/expand of all content
  const [showContent, setShowContent] = useState(true);

  // Group bookings using your existing logic then flatten to events
  const groupedBookings = GroupBookingsByDateAndTimeRange(
    bookingsArray,
    getBookingDetail,
    answersData,
    timezone
  );
  const events = [];
  Object.entries(groupedBookings).forEach(([dateKey, timeRanges]) => {
    Object.entries(timeRanges).forEach(([timeRangeKey, bookingTypes]) => {
      Object.entries(bookingTypes).forEach(([bookingTypeTitle, group]) => {
        group.bookings.forEach((item, idx) => {
          events.push({
            id: `${item.booking.id}-${idx}`,
            date: dateKey,
            timeRange: timeRangeKey,
            bookingType: bookingTypeTitle,
            color: group.color,
            booking: item.booking,
            answer: item.answer,
          });
        });
      });
    });
  });
  // Sort events by date
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  // Group events by day, then time range, then booking type
  const eventsByDay = events.reduce((acc, event) => {
    if (!acc[event.date]) acc[event.date] = {};
    if (!acc[event.date][event.timeRange]) acc[event.date][event.timeRange] = {};
    if (!acc[event.date][event.timeRange][event.bookingType])
      acc[event.date][event.timeRange][event.bookingType] = [];
    acc[event.date][event.timeRange][event.bookingType].push(event);
    return acc;
  }, {});

  const today = new Date();

  // Format date with "Today" if applicable
  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    const options = { weekday: "short", year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString(undefined, options) + (isToday ? " - Today" : "");
  };

  // Toggle collapse for day group
  const toggleDayGroup = (dateKey) => {
    setExpandedDays((prev) => ({
      ...prev,
      [dateKey]: !prev[dateKey],
    }));
  };

  // Default expanded state for day groups: expand only today's day group.
  const defaultExpandedDays = {};
  Object.keys(eventsByDay).forEach((dateKey) => {
    const date = new Date(dateKey);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    defaultExpandedDays[dateKey] = isToday;
  });
  const [expandedDays, setExpandedDays] = useState(defaultExpandedDays);

  // Toggle collapse for time group (within a day)
  const toggleTimeGroup = (dateKey, timeRange) => {
    const key = `${dateKey}-${timeRange}`;
    setExpandedTimeGroups((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Default expanded state for time groups: for today's day, expand all.
  const defaultExpandedTimeGroups = {};
  Object.entries(eventsByDay).forEach(([dateKey, timeGroups]) => {
    const date = new Date(dateKey);
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
    if (isToday) {
      Object.keys(timeGroups).forEach((timeRange) => {
        defaultExpandedTimeGroups[`${dateKey}-${timeRange}`] = true;
      });
    }
  });
  const [expandedTimeGroups, setExpandedTimeGroups] = useState(defaultExpandedTimeGroups);

  // Cancel individual booking
  const handleCancelBooking = (selectedBooking) => {
    if (!selectedBooking) return;
    setSelectedBookings([selectedBooking]);
    setShowCancelModal(true);
  };

  // Cancel all bookings for a given booking type group within a time range and day
  const handleCancelAllBookings = (dateKey, timeRange, bookingType) => {
    const bookingsGroup = eventsByDay[dateKey][timeRange][bookingType];
    const allBookings = bookingsGroup.map((e) => e.booking);
    setSelectedBookings(allBookings);
    setShowCancelModal(true);
  };

  // BookingTypeHeader component: shows booking type name, number of attendees, and a menu for "Cancel All".
  const BookingTypeHeader = ({ dateKey, timeRange, bType, eventsList }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const handleMenuOpen = (e) => {
      e.stopPropagation();
      setAnchorEl(e.currentTarget);
    };
    const handleMenuClose = () => {
      setAnchorEl(null);
    };
    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
        {/* Left side: Attendee Chip and booking type title */}
        <Stack direction="row" alignItems="center" spacing={1}>

          <Typography variant="body1" fontWeight="bold">
            {bType}
          </Typography>
          <Tooltip title={`Attendees: ${eventsList.length}`} arrow>
            <Chip
              label={eventsList.length}
              size="small"
              sx={{ bgcolor: "#E8F5E9", color: "#2E7D32", fontSize: "0.75rem" }}
            />
          </Tooltip>
        </Stack>
        {/* Right side: Menu icon */}
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClose();
              handleCancelAllBookings(dateKey, timeRange, bType);
            }}
          >
            Cancel All
          </MenuItem>
        </Menu>
      </Stack>
    );
  };

  return (
    <Box sx={{ maxWidth: 700, mx: "auto", mt: 3, px: 2 }}>
      {/* Main Header with collapse/expand toggle */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 2, cursor: "pointer" }}
        onClick={() => setShowContent(!showContent)}
      >
        <EventNoteIcon color="primary" />
        <Typography variant="h6" flex={1}>
          {bookingType.charAt(0).toUpperCase() + bookingType.slice(1)} appointments ({currentYear})
        </Typography>
        {showContent ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Stack>

      {/* Collapsible Content */}
      <Collapse in={showContent} timeout="auto" unmountOnExit>
        {Object.keys(eventsByDay).length === 0 ? (
          <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", p: 2 }}>
            No upcoming records.
          </Typography>
        ) : (
          Object.entries(eventsByDay).map(([dateKey, timeGroups]) => {
            // Unique event count for the day: count distinct booking type groups across all time ranges.
            const dayUnique = Object.values(timeGroups).reduce(
              (sum, timeGroup) => sum + Object.keys(timeGroup).length,
              0
            );
            return (
              <Box key={dateKey} sx={{ mb: 1 }}>
                {/* Day Header */}
                <Box
                  onClick={() => toggleDayGroup(dateKey)}
                  sx={{
                    bgcolor: "grey.200",
                    p: 1,
                    pl: 2,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" fontWeight="bold" fontSize="0.8rem">
                      {getFormattedDate(dateKey)}
                    </Typography>
                    <Tooltip title={`Events: ${dayUnique}`} arrow>
                    <Chip
                      label={`Total: ${dayUnique}`}
                      size="small"
                      sx={{ bgcolor: "#e0f7fa", color: "#00695c", fontSize: "0.75rem" }}
                    />
                    </Tooltip>
                  </Stack>
                  {/* {expandedDays[dateKey] ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )} */}
                </Box>
                <Collapse in={expandedDays[dateKey]} timeout="auto" unmountOnExit>
                  {Object.entries(timeGroups).map(([timeRange, bookingTypeGroups]) => {
                    // Unique event count for this time range: count distinct booking type groups.
                    const timeGroupUnique = Object.keys(bookingTypeGroups).length;
                    const timeGroupKey = `${dateKey}-${timeRange}`;
                    return (
                      <Box key={timeGroupKey} sx={{ pl: 2, pt: 0.5 }}>
                        {/* Time Range Header */}
                        <Box
                          onClick={() => toggleTimeGroup(dateKey, timeRange)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1,
                            cursor: "pointer",
                            border: "1px solid",
                            borderColor: "grey.200",
                            fontSize: "1rem",
                            "&:hover": { bgcolor: "grey.50" },
                          }}
                        >
                          <Stack direction="row" alignItems="center">
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              color="text.secondary"
                              sx={{ fontSize: ".85rem" }}
                            >
                              {timeRange}
                            </Typography>
                          </Stack>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Tooltip title={`Events: ${timeGroupUnique}`} arrow>
                            <Chip
                              label={`${timeGroupUnique}`}
                              size="small"
                              sx={{ bgcolor: "#e0f7fa", color: "#00695c", fontSize: "0.75rem" }}
                              />
                            </Tooltip>
                            {/* {expandedTimeGroups[timeGroupKey] ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )} */}
                          </Stack>
                        </Box>
                        <Collapse in={expandedTimeGroups[timeGroupKey]} timeout="auto" unmountOnExit>
                          {Object.entries(bookingTypeGroups).map(([bType, eventsList]) => (
                            <Box key={`${timeGroupKey}-${bType}`} sx={{ pl: 3, pt: 0.3 }}>
                              {/* Booking Type Header with menu for Cancel All */}
                              <BookingTypeHeader
                                dateKey={dateKey}
                                timeRange={timeRange}
                                bType={bType}
                                eventsList={eventsList}
                              />
                              {/* Render individual events */}
                              <List>
                                {eventsList.map((event) =>
                                  event?.booking?.source !== "google" && (
                                    <ListItem
                                      key={event.id}
                                      sx={{ pl: 3, alignItems: "center", py: 1 }}
                                      secondaryAction={
                                        <IconButton
                                          edge="end"
                                          color="error"
                                          onClick={() => handleCancelBooking(event.booking)}
                                          sx={{ p: 0.5 }}
                                        >
                                          <CloseIcon />
                                        </IconButton>
                                      }
                                    >
                                      <ListItemText
                                        primary={
                                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            <strong>Name:</strong> {event.booking?.inviteeName || "N/A"}
                                          </Typography>
                                        }
                                        secondary={
                                          <Stack spacing={0.5}>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Email:</strong> {event.booking?.inviteeEmail || "N/A"}
                                            </Typography>
                                            {event.booking.inviteePhoneNumbers &&
                                              event.booking.inviteePhoneNumbers.length > 0 && (
                                                <Typography variant="body2" color="text.secondary">
                                                  <strong>Phone:</strong>{" "}
                                                  {event.booking.inviteePhoneNumbers.map((phone, index) => (
                                                    <React.Fragment key={phone}>
                                                      <a
                                                        href={`tel:${phone}`}
                                                        style={{ textDecoration: "none", color: "inherit" }}
                                                      >
                                                        {phone}
                                                      </a>
                                                      {index < event.booking.inviteePhoneNumbers.length - 1 ? ", " : ""}
                                                    </React.Fragment>
                                                  ))}
                                                </Typography>
                                              )}
                                            {event.answer && (
                                              <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontStyle: "italic" }}
                                              >
                                                {`Answer: ${event.answer}`}
                                              </Typography>
                                            )}
                                          </Stack>
                                        }
                                      />
                                    </ListItem>
                                  )
                                )}
                              </List>
                            </Box>
                          ))}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })
        )}
        <CancelSchedule
          currentData={selectedBookings} 
          showCancelModal={showCancelModal}
          setShowCancelModal={setShowCancelModal}
          answersData={answersData}
        />
      </Collapse>
    </Box>
  );
};

export default BookingRecord;
